import { hinweisen } from "./bestaetigen";
// useContactGeneralForm — Logik des „Allgemein"-Tabs der Kontakt-Detailansicht.
//
// Portiert die voll funktionsfähige Datenanbindung aus der alten
// `ContactDetails.tsx` (State, Laden, Editieren, Speichern, Ownership,
// Kommunikation) in einen wiederverwendbaren Hook, damit
// `ContactDetailsRedesign.tsx` reine Präsentation bleibt. Bewusst kein
// JSX-Layout hier — nur Daten + Handler (Toasts ausgenommen).
//
// Wiederverwendete Bausteine: `apiToForm` (contactDetailMapper),
// `speicherPayload`/`nurGeaenderteFelder` (kontaktSpeicherPayload),
// `fetchLegalFormOptions` (legalFormOptions), `useReferenceData` (FO/BO).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";
import { katalogLaden } from "../../utils/katalogCache";
import {
  apiToForm,
  type SalutationOption,
  type TitleOption,
} from "../../utils/contactDetailMapper";
import { nurGeaenderteFelder, speicherPayload } from "./kontaktSpeicherPayload";
import { kontoinhaberVorschlag } from "../../utils/kontoinhaber";
import {
  fetchLegalFormOptions,
  type LegalFormOption,
} from "../../utils/legalFormOptions";
import { useReferenceData } from "../../context/ReferenceDataContext";
import type { ContactDetailsFormValues } from "../../types/Contact";
import { eigenenStandMelden } from "../../utils/kontaktStand";

type CommLabel = "privat" | "geschäftlich";

const legalFormLabel = (
  lf: ContactDetailsFormValues["legalForm"] | ContactDetailsFormValues["oldLegalForm"],
): string => {
  if (lf && typeof lf === "object") return lf.legalForm ?? "";
  return typeof lf === "string" ? lf : "";
};

// Social-Media-Kanäle speichern sofort (eigene Endpoints) und zählen daher NICHT
// zum „ungespeichert"-Zustand des Speichern-Buttons. `homepage` gehört NICHT
// dazu: die Website wird als normales Feld in der Kommunikation bearbeitet und
// mit dem Speichern-Knopf geschrieben — bliebe sie hier, fiele die Änderung aus
// dem Dirty-Vergleich, die Speicherleiste erschiene nie und der Wert wäre weg.
const SOCIAL_TYPES = new Set([
  "linkedin", "instagram", "facebook", "tiktok", "xing", "youtube",
]);

// Projektion für das Dirty-Tracking: nur die Felder, die der Speichern-Button
// persistiert. Sofort-gespeicherte Felder (Erreichbarkeit, Ownership, Social)
// werden ausgeklammert, damit sie die Bottom-Bar nicht fälschlich auslösen.
const dirtyProjection = (cd: ContactDetailsFormValues | null): string => {
  if (!cd) return "";
  const {
    erreichbarkeit: _e,
    foUserId: _fo,
    boType: _bt,
    boUserId: _bu,
    boTeamId: _btm,
    relatedCommunications: _rc,
    relationsSrc: _rs,
    ...rest
  } = cd as ContactDetailsFormValues & { canWrite?: boolean };
  delete (rest as { canWrite?: boolean }).canWrite;
  delete (rest as { canReassignOwnership?: boolean }).canReassignOwnership;
  // Ebenfalls server-berechnet: sagt nur, ob die geschützten Angaben gezeigt
  // werden dürfen — kein Formularwert, den man speichern könnte.
  delete (rest as { canReadProtectedFields?: boolean }).canReadProtectedFields;
  // Dasselbe für „CO intern": zwei server-berechnete Flags, keine
  // Formularwerte. Blieben sie in der Projektion, erschiene die Speicherleiste
  // allein deshalb, weil der Server sie mitschickt.
  delete (rest as { canReadCoIntern?: boolean }).canReadCoIntern;
  delete (rest as { canWriteCoIntern?: boolean }).canWriteCoIntern;
  // Schloss ohne Schreibrecht (30.09.2026): wer zuständig ist + Stand der
  // eigenen Freigabe-Anfrage — server-berechnet, kein Formularwert.
  delete (rest as { schreibSperre?: unknown }).schreibSperre;
  // Der Kontaktstatus wird über die Status-Pille SOFORT gespeichert
  // (PATCH /contact/:id/status) und vom Speichern-Button nie angefasst. Bliebe
  // er in der Projektion, erschiene nach jeder Statusänderung die Speicherleiste
  // — für etwas, das längst geschrieben ist.
  delete (rest as { status?: unknown }).status;
  delete (rest as { extraStatuses?: unknown }).extraStatuses;
  const communications = (rest.communications ?? []).filter(
    (c: { type?: string }) => !SOCIAL_TYPES.has(c.type ?? ""),
  );
  return JSON.stringify({ ...rest, communications });
};

export function useContactGeneralForm(
  contactId: number,
  hasContact: boolean,
  opts?: { cleanupWrite?: boolean },
) {
  // #6: In der Datenbereinigung darf editiert werden, auch ohne sonstiges
  // Schreibrecht — Speichern läuft dann über den scope-gegateten Cleanup-Endpoint
  // (PUT /contact/:id/cleanup), ohne per-Kontakt-Ownership-Check.
  const cleanupWrite = opts?.cleanupWrite ?? false;
  const { appTeams, users } = useReferenceData();
  const toasterId = useId("cd-redesign-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const [contactData, setContactData] =
    useState<ContactDetailsFormValues | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  /** Der zuletzt geladene Kontakt in API-Rohform (für das Anlage-Formular). */
  const [rohKontakt, setRohKontakt] = useState<Record<string, any> | null>(null);
  // Baseline-Snapshot (Dirty-Projektion) zum Zeitpunkt des Ladens / letzten Speicherns.
  const [baseline, setBaseline] = useState<string>("");
  // Der Dirty-Zustand zusätzlich als Ref: `assignOwnership` muss ihn beim
  // EINTREFFEN der PUT-Antwort prüfen, nicht beim Absenden. Die Zuweisung steht
  // unten direkt bei `isDirty` und führt damit jede Formularänderung synchron mit.
  const isDirtyRef = useRef(false);
  // Der geladene Serverstand als OBJEKT — Bezugspunkt des Feld-Vergleichs beim
  // Speichern (siehe kontaktSpeicherPayload). `baseline` daneben bleibt die
  // String-Projektion für das Dirty-Flag: Die taugt zur Ja/Nein-Frage
  // „irgendetwas geändert?", nicht zur Frage „welches Feld genau?".
  const geladenerStandRef = useRef<ContactDetailsFormValues | null>(null);
  // Der aktuelle Formularstand als Ref: Nach dem Speichern muss entscheidbar
  // sein, ob WÄHRENDDESSEN weitergetippt wurde — maßgeblich ist der Stand beim
  // EINTREFFEN der Antwort, nicht der beim Absenden. Jede Formularänderung legt
  // ein neues Objekt an, der Vergleich auf Objektgleichheit genügt also.
  const contactDataRef = useRef<ContactDetailsFormValues | null>(null);
  contactDataRef.current = contactData;

  const [anredeOptions, setAnredeOptions] = useState<SalutationOption[]>([]);
  const [titelOptions, setTitelOptions] = useState<TitleOption[]>([]);
  const [legalFormOptions, setLegalFormOptions] = useState<LegalFormOption[]>([]);
  const [legalFormLoading, setLegalFormLoading] = useState(false);
  const [gesellschaftsform, setGesellschaftsform] = useState("");
  const [oldLegalForm, setOldLegalForm] = useState("");

  // --- Kontakt laden (GET /contact/:id?include=… → apiToForm) ---
  useEffect(() => {
    if (!hasContact) return;
    const ctrl = new AbortController();
    setLoading(true);
    setLadeFehler(null);
    // `relations-deep` zieht zusätzlich `relationsSrc.contactDest.communications`
    // für die Kontaktmöglichkeiten im Verknüpfungen-Block (read-only).
    fetch(apiUrl(`contact/${contactId}`, { include: "relations,relations-deep" }), {
      signal: ctrl.signal,
    })
      .then(async r => {
        if (r.ok) return r.json();
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${r.status}`);
      })
      .then((data) => {
        // Rohform für das Anlage-Formular der Akte (AkteAllgemeinAnlage,
        // `vorgeladen`) — als Kopie, bevor `apiToForm` sie anfasst. Spart die
        // zweite, gleiche Abfrage (Befund 28.09.2026).
        setRohKontakt(typeof structuredClone === "function" ? structuredClone(data) : JSON.parse(JSON.stringify(data)));
        const form = apiToForm(data) as ContactDetailsFormValues;
        setContactData(form);
        geladenerStandRef.current = form;
        setBaseline(dirtyProjection(form));
        setGesellschaftsform(legalFormLabel(form.legalForm));
        setOldLegalForm(legalFormLabel(form.oldLegalForm));
      })
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setLadeFehler((e as Error).message);
        setContactData(null);
        setRohKontakt(null);
        if ((e as Error).message === "Diese Akte ist der Leitung vorbehalten") {
          void hinweisen({ titel: "Diese Akte ist der Leitung vorbehalten", text: "Nur die Leitung kann Partnerakten öffnen und bearbeiten." });
          return;
        }
        console.error("Kontakt laden fehlgeschlagen", e);
        dispatchToast(
          <Toast>
            <ToastTitle>Kontakt konnte nicht geladen werden</ToastTitle>
            <ToastBody>Bitte Seite neu laden oder später erneut versuchen.</ToastBody>
          </Toast>,
          { intent: "error" },
        );
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, hasContact, reloadTick]);

  // --- Rechtsform-Optionen ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLegalFormLoading(true);
        const opts = await fetchLegalFormOptions();
        if (mounted) setLegalFormOptions(opts);
      } catch (e) {
        console.error("Rechtsformen laden fehlgeschlagen", e);
        if (mounted) setLegalFormOptions([]);
      } finally {
        if (mounted) setLegalFormLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // --- Anrede- / Titel-Optionen ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Kataloge geteilt mit der Maske (utils/katalogCache.ts).
        const [sal, tit] = await Promise.allSettled([
          katalogLaden<SalutationOption[]>("contactsalutation"),
          katalogLaden<TitleOption[]>("contact-title"),
        ]);
        if (!mounted) return;
        if (sal.status === "fulfilled") setAnredeOptions(sal.value);
        if (tit.status === "fulfilled") setTitelOptions(tit.value);
      } catch (e) {
        console.error("Anrede/Titel laden fehlgeschlagen", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // --- generische Feld-Bindung ---
  const setField = useCallback(
    (key: keyof ContactDetailsFormValues, value: unknown) => {
      setContactData((cd) => (cd ? { ...cd, [key]: value } : cd));
    },
    [],
  );

  // --- Kommunikation: privat/geschäftlich (kind-first, Label-Fallback) ---
  // Strukturierte Art (ContactCommunication.kind, ASCII) gewinnt; Bestand ohne
  // kind wird weiter über das Label gefunden. Geschrieben wird IMMER kind —
  // das Label bleibt Freitext-Bezeichnung und wird nicht mehr überschrieben.
  const kindOf = (label: CommLabel): "privat" | "geschaeftlich" =>
    label === "privat" ? "privat" : "geschaeftlich";

  const commValue = useCallback(
    (type: string, label: CommLabel): string => {
      const comms = contactData?.communications ?? [];
      const kind = kindOf(label);
      const byKind = comms.find((c: any) => c.type === type && c.kind === kind);
      if (byKind?.value != null) return byKind.value;
      const labeled = comms.find(
        (c: any) => c.type === type && c.kind == null && (c.label ?? "").toLowerCase() === label,
      );
      if (labeled?.value != null) return labeled.value;
      if (label === "privat") {
        const legacy = comms.find((c: any) => c.type === type && c.kind == null && c.isDefault);
        if (legacy?.value != null) return legacy.value;
      }
      return "";
    },
    [contactData],
  );

  const setComm = useCallback((type: string, label: CommLabel, value: string) => {
    setContactData((cd) => {
      if (!cd) return cd;
      const comms = [...(cd.communications || [])];
      const kind = kindOf(label);
      let idx = comms.findIndex((c: any) => c.type === type && c.kind === kind);
      if (idx === -1) {
        idx = comms.findIndex(
          (c: any) => c.type === type && c.kind == null && (c.label ?? "").toLowerCase() === label,
        );
      }
      // Erste Bearbeitung eines privat-Feldes trifft ggf. eine alte Default-Zeile ohne Label.
      if (idx === -1 && label === "privat") {
        idx = comms.findIndex(
          (c: any) => c.type === type && c.kind == null && c.isDefault && !c.label,
        );
      }
      if (idx !== -1) {
        // Bestandszeile: Wert + kind nachziehen; Label und isDefault bleiben —
        // die frühere implizite Kopplung „privat = Standard" ist entfernt
        // (explizite Regel: Standard nur über Checkbox/Server-Regeln, 2.5).
        comms[idx] = { ...comms[idx], value, kind };
      } else {
        // Neue Zeile: Standard nur, wenn es noch KEINE Zeile dieses Typs gibt
        // (sonst würde der Save ein bestehendes Default verdrängen).
        const ersteDesTyps = !comms.some((c: any) => c.type === type);
        comms.push({ type, value, kind, label: null, isDefault: ersteDesTyps });
      }
      return { ...cd, communications: comms };
    });
  }, []);

  const faxValue =
    contactData?.communications?.find((c: any) => c.type === "fax" && c.isDefault)
      ?.value ?? "";

  const setFax = useCallback((value: string) => {
    setContactData((cd) => {
      if (!cd) return cd;
      const comms = [...(cd.communications || [])];
      const idx = comms.findIndex((c: any) => c.type === "fax" && c.isDefault);
      if (idx !== -1) comms[idx] = { ...comms[idx], value };
      else comms.push({ type: "fax", value, isDefault: true });
      return { ...cd, communications: comms };
    });
  }, []);

  // --- Firma-Kommunikation: ein einfacher Default-Eintrag je Typ (kein
  //     privat/geschäftlich-Split; mehrere Einträge via „verwalten"-Modal). ---
  const commValueFirma = useCallback(
    (type: string): string => {
      const comms = contactData?.communications ?? [];
      const def =
        comms.find((c: any) => c.type === type && c.isDefault) ??
        comms.find((c: any) => c.type === type);
      return def?.value ?? "";
    },
    [contactData],
  );

  const setCommFirma = useCallback((type: string, value: string) => {
    setContactData((cd) => {
      if (!cd) return cd;
      const comms = [...(cd.communications || [])];
      let idx = comms.findIndex((c: any) => c.type === type && c.isDefault);
      if (idx === -1) idx = comms.findIndex((c: any) => c.type === type);
      if (idx !== -1) comms[idx] = { ...comms[idx], value, isDefault: true };
      else comms.push({ type, value, label: "", isDefault: true });
      return { ...cd, communications: comms };
    });
  }, []);

  /**
   * Eine WEITERE Kommunikationszeile anlegen — ohne die vorhandene zu
   * überschreiben.
   *
   * `setCommFirma` pflegt die Standardzeile je Art; das ist richtig für „die"
   * Telefonnummer der Firma. Ein Impressum nennt aber regelmäßig mehrere
   * (Zentrale, Vertrieb, info@, datenschutz@), und `ContactCommunication` kann
   * beliebig viele Zeilen je Art führen. Ohne diesen Weg müsste der Anwender
   * sich beim Übernehmen für eine entscheiden und den Rest abtippen.
   *
   * Dubletten werden übersprungen: Derselbe Wert zweimal in der Liste wäre
   * keine zusätzliche Angabe, sondern eine Zeile zum Wegräumen.
   */
  const addCommFirma = useCallback((type: string, value: string, label?: string) => {
    const wert = (value ?? "").trim();
    if (!wert) return;
    setContactData((cd) => {
      if (!cd) return cd;
      const comms = [...(cd.communications || [])];
      const gleich = comms.some(
        (c: any) =>
          c.type === type &&
          String(c.value ?? "").trim().toLowerCase() === wert.toLowerCase(),
      );
      if (gleich) return cd;
      // Die erste Zeile einer Art ist die Standardzeile; jede weitere nicht.
      const hatSchon = comms.some((c: any) => c.type === type);
      comms.push({ type, value: wert, label: label ?? "", isDefault: !hatSchon });
      return { ...cd, communications: comms };
    });
  }, []);

  // Optimistisches Merge nach Social-Media-Save (POST/PUT geschieht in SocialMediaIcon).
  const upsertCommunication = useCallback((newComm: any) => {
    setContactData((cd) => {
      if (!cd) return cd;
      const comms = cd.communications || [];
      const exists = comms.find((c: any) => c.id === newComm.id);
      const next = exists
        ? comms.map((c: any) => (c.id === newComm.id ? newComm : c))
        : [...comms, newComm];
      return { ...cd, communications: next };
    });
  }, []);

  // --- Speichern: geänderte Felder ermitteln, PUT, Serverstand übernehmen ---
  // Speichert die Stammdaten. Gibt `true` bei Erfolg zurück, `false` bei Fehler
  // oder wenn nichts zu speichern war (kein contactData / fehlendes Schreibrecht).
  // Der Rückgabewert erlaubt es Aufrufern (z. B. der Altkunden-Abschließen-
  // Orchestrierung), den nächsten Schritt nur bei Erfolg auszuführen; bestehende
  // Aufrufer ignorieren ihn unverändert.
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!contactData) return false;
    if (!cleanupWrite && (contactData as { canWrite?: boolean }).canWrite === false)
      return false;

    // Nur die Felder mitschicken, die sich seit dem Laden geändert haben — sonst
    // trüge der Body auch jedes unangetastete Feld mit dem Stand vom Öffnen der
    // Akte und setzte damit die Änderungen eines Kollegen zurück (Begründung in
    // kontaktSpeicherPayload). Ohne bekannten Ladestand geht der volle Stand raus
    // wie früher: zu viel zu schreiben ist harmloser, als eine Änderung zu verlieren.
    const geladen = geladenerStandRef.current;
    // Der Katalog liegt nur hier — die Ableitung Anrede → Geschlecht braucht
    // das Label zur ID. Beide Stände laufen durch dieselbe Auflösung, sonst
    // sähe der Diff eine Änderung, wo keine ist.
    const anredeLabel = (id: string | null | undefined) =>
      anredeOptions.find((o) => String(o.id) === String(id ?? ""))?.salutation ?? undefined;
    // Vorbelegter Kontoinhaber (Name in Kontoschreibweise, ohne
    // Rechtsform-Kürzel), siehe utils/kontoinhaber.ts.
    const kontoinhaberFuer = (stand: ContactDetailsFormValues) => {
      const istFirma = (stand.typ ?? stand.type?.type ?? "Firma") === "Firma";
      return kontoinhaberVorschlag(stand, istFirma);
    };
    const vollerStand = speicherPayload(contactData, anredeLabel, kontoinhaberFuer);
    const payload = geladen
      ? nurGeaenderteFelder(vollerStand, speicherPayload(geladen, anredeLabel, kontoinhaberFuer))
      : vollerStand;
    const gesendeterStand = contactData;

    const meldeGespeichert = () =>
      dispatchToast(
        <Toast>
          <ToastTitle>Gespeichert</ToastTitle>
          <ToastBody>Der Kontakt wurde erfolgreich gespeichert.</ToastBody>
        </Toast>,
        { intent: "success" },
      );

    // Nach dem Schreiben ist der Serverstand maßgeblich, nicht der eigene: Er
    // trägt zusätzlich, was ein Kollege inzwischen geändert hat. Ohne den Refetch
    // bliebe die Maske auf ihrem Ladestand stehen und zeigte fremde Änderungen
    // erst nach einem Neuladen — obwohl sie gerade eben erhalten geblieben sind.
    // Wurde während des Speicherns weitergetippt, bleibt er aus: der Ladepfad
    // ersetzt `contactData` vollständig und verwürfe die frische Eingabe.
    const serverstandUebernehmen = () => {
      geladenerStandRef.current = gesendeterStand;
      setBaseline(dirtyProjection(gesendeterStand));
      if (contactDataRef.current === gesendeterStand) setReloadTick((t) => t + 1);
    };

    // Nichts geändert — etwa weil nur Branchen oder Arbeitgeber angefasst wurden,
    // die über eigene Endpoints laufen. Ein PUT mit leerem Body schriebe nichts
    // und hinterließe nur einen Protokolleintrag ohne Änderung; die Rückmeldung
    // bleibt trotzdem dieselbe, denn aus Sicht des Anwenders ist die Akte
    // gespeichert.
    if (Object.keys(payload).length === 0) {
      serverstandUebernehmen();
      meldeGespeichert();
      return true;
    }

    try {
      const res = await fetch(
        apiUrl(
          cleanupWrite
            ? `contact/${contactId}/cleanup`
            : `contact/${contactId}`,
        ),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) throw new Error(`PUT ${res.status}`);
      // Eigener Kopf-PUT zählt den Bearbeitungsstand hoch — dem Formular der
      // Akte melden, dass das kein Kollege war (utils/kontaktStand.ts).
      eigenenStandMelden(contactId, ((await res.json()) as { version?: unknown } | null)?.version);
      serverstandUebernehmen();
      meldeGespeichert();
      return true;
    } catch (e) {
      console.error("Speichern fehlgeschlagen", e);
      dispatchToast(
        <Toast>
          <ToastTitle>Fehler</ToastTitle>
          <ToastBody>Speichern fehlgeschlagen.</ToastBody>
        </Toast>,
        { intent: "error" },
      );
      return false;
    }
  }, [contactData, contactId, dispatchToast, cleanupWrite, anredeOptions]);

  // --- Ownership (FO/BO) via PUT /contact/:id/ownership ---
  const assignOwnership = useCallback(
    async (next: {
      foUserId?: string | null;
      /** Mehrere FO (28.09.2026): alle FO, erster = primär. Hat Vorrang vor foUserId. */
      foUserIds?: string[];
      boType?: "NONE" | "TEAM";
      boTeamId?: string | null;
    }) => {
      if (!contactId) return;
      const cd = contactData;
      const foListeNeu = next.foUserIds?.map((x) => x.trim()).filter(Boolean);
      const foUserId = (foListeNeu?.[0] ?? next.foUserId ?? cd?.foUserId ?? "").trim();
      const boType = next.boType ?? (cd?.boType as "NONE" | "TEAM") ?? "NONE";
      const boTeamId = next.boTeamId ?? cd?.boTeamId ?? null;

      const bo =
        boType === "TEAM" && boTeamId
          ? { type: "TEAM" as const, id: boTeamId }
          : { type: "NONE" as const, id: null };

      const snapshot = {
        foUserId: cd?.foUserId,
        foUserIds: cd?.foUserIds,
        boType: cd?.boType,
        boTeamId: cd?.boTeamId,
      };
      setContactData((c) =>
        c
          ? {
              ...c,
              foUserId: foUserId || c.foUserId,
              ...(foListeNeu?.length ? { foUserIds: foListeNeu } : {}),
              boType: bo.type,
              boTeamId: bo.type === "TEAM" ? bo.id : null,
            }
          : c,
      );

      try {
        // foUserId ist optional — nur mitschicken, wenn vorhanden. So lässt sich der BO
        // auch auf Kontakten OHNE FO setzen (Backend lässt den FO dann unverändert).
        const payload: { foUserId?: string; foUserIds?: string[]; bo: typeof bo } = { bo };
        if (foUserId) payload.foUserId = foUserId;
        if (foListeNeu?.length) payload.foUserIds = foListeNeu;
        const res = await fetch(apiUrl(`contact/${contactId}/ownership`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (res.status === 403) {
          setContactData((c) => (c ? { ...c, ...snapshot } : c));
          dispatchToast(
            <Toast>
              <ToastTitle>Keine Berechtigung</ToastTitle>
              <ToastBody>
                Nur Eigentümer (FO), GF oder Admin dürfen die Zuordnung ändern.
              </ToastBody>
            </Toast>,
            { intent: "error" },
          );
          return;
        }
        if (!res.ok) throw new Error(`PUT ownership ${res.status}`);
        dispatchToast(
          <Toast>
            <ToastTitle>Zuordnung gespeichert</ToastTitle>
          </Toast>,
          { intent: "success" },
        );
        // Nach Erfolg neu laden → Server berechnet canWrite/canReassignOwnership frisch.
        // Macht der BO-Wechsel den Kontakt für den Nutzer schreibbar (richtiges Team),
        // wird er sofort editierbar — ohne manuelles Neuladen.
        //
        // ABER nur bei sauberem Formular: der Lade-Effekt ersetzt `contactData`
        // vollständig. Die Zuordnung wird seit dem Umzug in den Kopf aus JEDEM
        // Reiter geändert und damit viel häufiger angefasst als früher das
        // Formularfeld — ein Refetch würde ungespeicherte Eingaben verwerfen.
        //
        // Maßgeblich ist der Stand JETZT, nicht der beim Chip-Klick: wer während
        // des laufenden PUT weitertippt, hat ein dirty gewordenes Formular — ein
        // eingefrorener Snapshot hielte es für sauber und der Refetch würde die
        // frische Eingabe verwerfen. Darum der Ref.
        if (!isDirtyRef.current) setReloadTick((t) => t + 1);
      } catch (e) {
        console.error("Ownership-Zuordnung fehlgeschlagen", e);
        setContactData((c) => (c ? { ...c, ...snapshot } : c));
        dispatchToast(
          <Toast>
            <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
            <ToastBody>Die Zuordnung konnte nicht übernommen werden.</ToastBody>
          </Toast>,
          { intent: "error" },
        );
      }
    },
    [contactData, contactId, dispatchToast],
  );

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  /**
   * Neu laden, ABER nur bei sauberem Formular.
   *
   * Für Bereiche, die SOFORT speichern und dabei unmittelbar neben Feldern
   * stehen, die erst am Speichern-Knopf hängen — im Abschnitt „Vertretung
   * und Bevollmächtigter“ trifft beides erstmals in EINER Karte aufeinander.
   * Der Lade-Effekt ersetzt `contactData` vollständig; ein Refetch verwürfe
   * die gerade angekreuzten Verfügungen und die getippten Namen der
   * Bevollmächtigten kommentarlos. Dieselbe Falle wie bei `assignOwnership`,
   * und aus demselben Grund über den Ref: maßgeblich ist der Stand beim
   * EINTREFFEN der Antwort, nicht der beim Klick.
   *
   * Bleibt der Refetch aus, muss der aufrufende Bereich seine eigene
   * Änderung lokal fortschreiben — sonst sähe der Anwender nach einem
   * erfolgreichen Speichern nichts passieren. Ob das nötig ist, sagt der
   * Rückgabewert: `true` = neu geladen, `false` = ausgelassen.
   */
  const reloadWennSauber = useCallback(() => {
    if (isDirtyRef.current) return false;
    setReloadTick((t) => t + 1);
    return true;
  }, []);

  const isDirty = useMemo(
    () =>
      contactData != null && baseline !== "" && dirtyProjection(contactData) !== baseline,
    [contactData, baseline],
  );
  // Bewusst im Render und nicht in einem Effekt: ein Effekt liefe erst nach dem
  // Commit und käme für eine Antwort, die im selben Microtask eintrifft, zu spät.
  isDirtyRef.current = isDirty;
  const canWrite =
    cleanupWrite ||
    ((contactData as { canWrite?: boolean } | null)?.canWrite ?? false);
  // Separates Recht NUR für die FO/BO-Umverteilung (Verantwortlichkeit) — kann auch
  // dann true sein, wenn canWrite false ist (z. B. mitarbeiter auf fremdem Kontakt).
  const canReassignOwnership =
    (contactData as { canReassignOwnership?: boolean } | null)?.canReassignOwnership ?? false;

  return {
    contactData,
    setContactData,
    rohKontakt,
    loading,
    ladeFehler,
    isDirty,
    canWrite,
    canReassignOwnership,
    anredeOptions,
    titelOptions,
    legalFormOptions,
    legalFormLoading,
    gesellschaftsform,
    setGesellschaftsform,
    oldLegalForm,
    setOldLegalForm,
    users,
    appTeams,
    setField,
    commValue,
    setComm,
    commValueFirma,
    setCommFirma,
    faxValue,
    setFax,
    addCommFirma,
    upsertCommunication,
    handleSave,
    assignOwnership,
    reload,
    reloadWennSauber,
    toasterId,
  };
}
